import { FC, useState } from 'react'
import classNames from 'classnames'
import { FlexRow } from '@/atoms/FlexContainers'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { Popover } from '@/atoms/Popover'
import { CaptionMD, ParagraphLG, ParagraphSM, TitleLG } from '@/atoms/Text'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { useIsDarkMode } from '@/contexts/ThemeContext'
import { ProjectFundingInfoModal } from '@/organisms/ProjectFundingGoalDisplay/ProjectFundingInfoModal'
import { useIncrementer } from '@/services/GoalService/hooks/useIncrementer'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import useTheatricalGoalService from '@/views/TicketCheckoutViews/hooks/useTheatricalGoalService'

interface TheatricalPifGoalLineProps {
  theatricalRelease: TheatricalReleaseObject
  fullWidth?: boolean
  className?: string
  shouldUseIncrementer?: boolean
  isPossumTrotExperiment?: boolean
}

export const TheatricalPifGoalLine: FC<TheatricalPifGoalLineProps> = ({
  theatricalRelease,
  fullWidth = false,
  className,
  shouldUseIncrementer = false,
  isPossumTrotExperiment = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { t } = useTranslate('pif')
  const isDarkMode = useIsDarkMode()
  const { locale } = useLocale()
  const { name } = useProjectMeta()
  const slug = theatricalRelease?.theatricalSlug
  const { goalLineInfo } = useTheatricalGoalService({
    theatricalSlug: slug,
    releaseDate: theatricalRelease?.region?.releaseDate,
    isPossumTrotExperiment,
  })
  const headerTextColor = isDarkMode ? 'white' : 'core-gray-950'
  const paragraphTextColor = isDarkMode ? 'core-gray-300' : 'core-gray-500'
  const fullWidthClass = !fullWidth ? '' : 'lg:flex-row lg:gap-14'
  const incrementerNumber = useIncrementer(goalLineInfo.ticketsSold, 0.75)
  const incrementerPercent = Math.floor((incrementerNumber / goalLineInfo.currentMilestone) * 100)
  const ticketsSold = shouldUseIncrementer ? Math.ceil(incrementerNumber) : goalLineInfo?.ticketsSold
  const ticketPercent = incrementerPercent

  return (
    <div
      className={classNames('mb-6 lg:mb-16 bg-white md:rounded-xl px-4 md:px-6 border border-core-gray-300', className)}
    >
      <div className="flex flex-col lg:gap-6">
        <div className="flex w-full flex-row justify-between">
          <div
            className={classNames(
              'mg:mb-0 mb-3 flex w-full items-center justify-between gap-3 md:flex-row md:gap-0 flex-row',
            )}
          >
            <div className="flex flex-col gap-1">
              <FlexRow className="!items-baseline gap-1">
                <TitleLG color={isDarkMode ? 'white' : 'gray-950'} weight="bold" className="flex justify-between">
                  {goalLineInfo?.goalLineTitle}
                </TitleLG>
                {isPossumTrotExperiment && (
                  <Popover
                    className="!z-50"
                    title={t('info', 'Info')}
                    description={t(
                      'totalTicketsPIFDisclaimer',
                      'Total tickets are an estimate based on current box office sales. Pay it Forward is only included in the total when a free ticket is redeemed to watch in theaters.',
                    )}
                    icon={<CircleInfoIcon size={18} color="core-gray-400" />}
                    triggerComponent={<CircleInfoIcon size={18} color="core-gray-500" />}
                  />
                )}
              </FlexRow>
              {isPossumTrotExperiment && (
                <FlexRow className="!items-start gap-1">
                  <CaptionMD color="core-gray-600">
                    {t('possumTrotGoalTitle', '1 million tickets for 100k forgotten kids')}
                  </CaptionMD>
                  <Popover
                    title="Every Child Needs a Home"
                    description={t(
                      'possumTrotGoalDescription',
                      'There are about 100,000 children in the US foster system who are waiting to be adopted. While they wait, they live in multiple temporary foster houses.\n' +
                        'When these children age out of the system before finding a permanent family, the majority of them experience devastating outcomes involving addiction, homelessness, and incarceration.\n' +
                        'Help us send a loud message that "There should never be a child without a home."',
                    )}
                    triggerComponent={<CircleInfoIcon size={18} color="core-gray-500" />}
                  />
                </FlexRow>
              )}
            </div>
            {!isPossumTrotExperiment && (
              <Popover
                title={t('info', 'Info')}
                description={t(
                  'totalTicketsPIFDisclaimer',
                  'Total tickets are an estimate based on current box office sales. Pay it Forward is only included in the total when a free ticket is redeemed to watch in theaters.',
                )}
                icon={<CircleInfoIcon size={18} color="core-gray-400" />}
                triggerComponent={<CircleInfoIcon size={18} color="core-gray-500" />}
              />
            )}
          </div>
        </div>
        <div className={classNames('flex flex-col gap-1', fullWidthClass)}>
          <div className="flex flex-row justify-between lg:hidden">
            <div className="flex flex-col gap-1 lg:gap-2">
              <ParagraphSM color={headerTextColor} className="block lg:hidden" weight="semibold">
                {t('numberOfTickets', '{{numTickets}} Tickets', {
                  numTickets: new Intl.NumberFormat(locale).format(ticketsSold),
                })}
              </ParagraphSM>
            </div>
            <div className="flex flex-col gap-1 lg:gap-2">
              <ParagraphSM color={paragraphTextColor} className="block lg:hidden">
                {new Intl.NumberFormat(locale).format(goalLineInfo.currentMilestone)}{' '}
                {goalLineInfo?.isLastMilestone
                  ? t('totalTicketsGoalV6', 'Goal')
                  : t('goalLineMilestoneText', 'Milestone')}
              </ParagraphSM>
            </div>
          </div>
          <div className="flex basis-1/2 flex-col gap-2 lg:gap-6">
            <div className={classNames('w-full rounded-full', isDarkMode ? 'bg-core-gray-800' : 'bg-core-gray-200')}>
              <div
                className="h-2.5 rounded-full bg-oxide-700"
                style={{
                  width: `${ticketPercent > 100 ? '100%' : `${ticketPercent}%` ?? 0}`,
                }}
              />
            </div>
            <div className="flex flex-row justify-between">
              <div className="hidden flex-col gap-1 lg:flex lg:gap-2">
                <TitleLG color={headerTextColor} className="hidden text-xl lg:block xl:text-2xl" weight="semibold">
                  {t('numberOfTickets', '{{numTickets}} Tickets', {
                    numTickets: new Intl.NumberFormat(locale).format(ticketsSold),
                  })}
                </TitleLG>
                <div className="flex flex-row items-center">
                  <ParagraphLG color={paragraphTextColor} className="hidden text-base lg:block xl:text-lg">
                    {t('ofTicketsGoalV7', 'of {{numTickets}} {{goalText}}', {
                      numTickets: new Intl.NumberFormat(locale).format(goalLineInfo.currentMilestone),
                      goalText: goalLineInfo?.isLastMilestone
                        ? t('goalLineGoalPifText', 'Goal')
                        : t('goalLineMilestoneText', 'Milestone'),
                    })}
                  </ParagraphLG>
                </div>
              </div>
              <div className="flex flex-col justify-between gap-1 lg:ml-auto lg:gap-2">
                <ParagraphSM color={headerTextColor} className={`flex justify-start lg:hidden iPadPro105:hidden`}>
                  {t('percentOfTicketsGoal', '{{percentOfGoal}}% of Goal', {
                    percentOfGoal: ticketPercent,
                  })}
                </ParagraphSM>
                <TitleLG
                  color={headerTextColor}
                  className="hidden justify-start text-xl !font-semibold xl:text-2xl iPadPro105:flex"
                >
                  {t('percentOfTicketsGoal', '{{percentOfGoal}}% of Goal', {
                    percentOfGoal: ticketPercent,
                  })}
                </TitleLG>
              </div>

              <div className="lg:display min-[1042px]:hidden ml-auto flex flex-col gap-1 lg:gap-2 iPadPro105:hidden">
                <TitleLG
                  color={headerTextColor}
                  className="hidden justify-start text-xl !font-semibold lg:flex xl:text-2xl"
                >
                  {t('percentOfTicketsGoal', '{{percentOfGoal}}% of Goal', {
                    percentOfGoal: ticketPercent,
                  })}
                </TitleLG>
              </div>
            </div>
          </div>
          <ProjectFundingInfoModal
            onClose={() => setIsModalOpen(false)}
            show={isModalOpen}
            goalName={name}
            isTheatrical={true}
          />
        </div>
      </div>
    </div>
  )
}
